import { BlogArticle } from '../types';

// 每日聆聽訓練法 → CTA: /module/listening
const article: BlogArticle = {
  slug: 'daily-15-minute-listening-training',
  date: '2026-09-09',
  readingMinutes: 4,
  tags: ['聆聽', 'Listening', '英文', '溫書技巧'],

  locales: {
    zhHant: {
      title: '每日 15 分鐘：英文聆聽進步最快的訓練法',
      excerpt: '聽力差唔係因為你聽得少，而係因為你聽錯方法。「三遍精聽法」每日 15 分鐘，一個月就有分別。',
      keywords: '英文聆聽, listening practice, 精聽, 聽力訓練, DSE listening',
      contentHtml: `
        <p>好多同學「聽」咗好多英文 —— 煲劇、聽歌、睇 YouTube —— 但考試聆聽卷照樣失手。原因好簡單：<strong>泛聽練語感，精聽先練到聽力</strong>。考試要嘅係精聽能力。</p>

        <h2>精聽 vs 泛聽</h2>
        <table>
          <thead>
            <tr><th></th><th>泛聽</th><th>精聽</th></tr>
          </thead>
          <tbody>
            <tr><td>目的</td><td>語感、興趣</td><td>聽清每個字、理解細節</td></tr>
            <tr><td>材料</td><td>電影、歌、podcast</td><td>1–2 分鐘短音頻</td></tr>
            <tr><td>方法</td><td>輕鬆聽</td><td>反覆聽 + 對稿</td></tr>
          </tbody>
        </table>

        <h2>三遍精聽法（每日 15 分鐘）</h2>
        <ol>
          <li><strong>第 1 遍：盲聽（5 分鐘）</strong>—— 唔睇任何文字，聽 2–3 次，記低你聽到嘅關鍵字同主旨。</li>
          <li><strong>第 2 遍：對稿（5 分鐘）</strong>—— 睇住 transcript 再聽一次，圈出你之前聽唔到嘅位。呢啲位通常係連讀（linking）、弱讀（weak forms）或者唔識嘅字。</li>
          <li><strong>第 3 遍：跟讀（5 分鐘）</strong>—— 跟住音頻讀，模仿語速同語調。讀得出，先真正聽得明。</li>
        </ol>

        <div class="tip-box">
          <strong>💡 貼士：</strong>材料唔好貪長。一段 1–2 分鐘、你聽得明 70% 嘅音頻，係最好嘅訓練材料。完全聽唔明嘅太難，完全聽得明嘅冇進步空間。
        </div>

        <h2>材料點揀？</h2>
        <ul>
          <li><strong>DSE 考生</strong>：直接用 past paper 聆聽材料，熟習考試語速同口音</li>
          <li><strong>IELTS 考生</strong>：Cambridge 真題，或者 BBC 6 Minute English</li>
          <li><strong>日常練習</strong>：TED-Ed 短片（有字幕同 transcript）</li>
        </ul>

        <h2>冇材料？AI 幫你出題</h2>
        <p>StudyMind 嘅聆聽練習可以按你嘅程度生成音頻，仲會自動出題目考你聽唔聽得明：</p>
        <p><a class="blog-cta-button" href="/module/listening">🎧 免費開始聆聽訓練 →</a></p>

        <blockquote>聽力唔係天份，係每日 15 分鐘嘅累積。</blockquote>
      `,
    },

    zhHans: {
      title: '每天 15 分钟：英文听力进步最快的训练法',
      excerpt: '听力差不是因为你听得少，而是因为你听错了方法。「三遍精听法」每天 15 分钟，一个月就有分别。',
      keywords: '英文听力, listening practice, 精听, 听力训练, DSE listening',
      contentHtml: `
        <p>很多同学「听」了很多英文 —— 追剧、听歌、看 YouTube —— 但考试听力卷照样失手。原因很简单：<strong>泛听练语感，精听才练到听力</strong>。考试要的是精听能力。</p>

        <h2>精听 vs 泛听</h2>
        <table>
          <thead>
            <tr><th></th><th>泛听</th><th>精听</th></tr>
          </thead>
          <tbody>
            <tr><td>目的</td><td>语感、兴趣</td><td>听清每个字、理解细节</td></tr>
            <tr><td>材料</td><td>电影、歌、podcast</td><td>1–2 分钟短音频</td></tr>
            <tr><td>方法</td><td>轻松听</td><td>反复听 + 对稿</td></tr>
          </tbody>
        </table>

        <h2>三遍精听法（每天 15 分钟）</h2>
        <ol>
          <li><strong>第 1 遍：盲听（5 分钟）</strong>—— 不看任何文字，听 2–3 次，记下你听到的关键字和主旨。</li>
          <li><strong>第 2 遍：对稿（5 分钟）</strong>—— 看着 transcript 再听一次，圈出你之前听不到的地方。这些通常是连读（linking）、弱读（weak forms）或不认识的词。</li>
          <li><strong>第 3 遍：跟读（5 分钟）</strong>—— 跟着音频读，模仿语速和语调。读得出，才真正听得懂。</li>
        </ol>

        <div class="tip-box">
          <strong>💡 提示：</strong>材料不要贪长。一段 1–2 分钟、你听得懂 70% 的音频，是最好的训练材料。完全听不懂的太難，完全听得懂的没进步空间。
        </div>

        <h2>材料怎么选？</h2>
        <ul>
          <li><strong>DSE 考生</strong>：直接用 past paper 听力材料，熟习考试语速和口音</li>
          <li><strong>IELTS 考生</strong>：Cambridge 真题，或 BBC 6 Minute English</li>
          <li><strong>日常练习</strong>：TED-Ed 短片（有字幕和 transcript）</li>
        </ul>

        <h2>没材料？AI 帮你出题</h2>
        <p>StudyMind 的听力练习可以按你的程度生成音频，还会自动出题目考你听不听得懂：</p>
        <p><a class="blog-cta-button" href="/module/listening">🎧 免费开始听力训练 →</a></p>

        <blockquote>听力不是天分，是每天 15 分钟的累积。</blockquote>
      `,
    },

    en: {
      title: '15 Minutes a Day: The Fastest Way to Improve Your English Listening',
      excerpt: "Weak listening isn't about listening too little — it's about listening the wrong way. The 3-pass intensive method, 15 minutes a day.",
      keywords: 'English listening, listening practice, intensive listening, DSE listening, IELTS',
      contentHtml: `
        <p>Many students "listen" to plenty of English — dramas, songs, YouTube — yet still freeze in listening exams. The reason is simple: <strong>extensive listening builds feel; only intensive listening builds skill</strong>. Exams test the latter.</p>

        <h2>Intensive vs extensive listening</h2>
        <table>
          <thead>
            <tr><th></th><th>Extensive</th><th>Intensive</th></tr>
          </thead>
          <tbody>
            <tr><td>Goal</td><td>Exposure, enjoyment</td><td>Catch every word, understand details</td></tr>
            <tr><td>Material</td><td>Movies, songs, podcasts</td><td>1–2 minute clips</td></tr>
            <tr><td>Method</td><td>Relaxed listening</td><td>Repeated passes + transcript</td></tr>
          </tbody>
        </table>

        <h2>The 3-pass method (15 min/day)</h2>
        <ol>
          <li><strong>Pass 1: Blind listening (5 min)</strong> — no text. Listen 2–3 times and note the keywords and gist you caught.</li>
          <li><strong>Pass 2: Check the transcript (5 min)</strong> — listen again while reading. Circle what you missed; these are usually linking, weak forms, or unknown words.</li>
          <li><strong>Pass 3: Shadow (5 min)</strong> — read along with the audio, matching its speed and intonation. If you can say it, you can hear it.</li>
        </ol>

        <div class="tip-box">
          <strong>💡 Tip:</strong> Don't go long — go right. A 1–2 minute clip you understand 70% of is perfect training material. Too easy means no growth; too hard means no traction.
        </div>

        <h2>Choosing material</h2>
        <ul>
          <li><strong>DSE students</strong>: past paper recordings — learn the exam's speed and accents</li>
          <li><strong>IELTS students</strong>: Cambridge practice tests, or BBC 6 Minute English</li>
          <li><strong>General practice</strong>: TED-Ed videos (subtitles + transcripts included)</li>
        </ul>

        <h2>No material? Let AI generate it</h2>
        <p>StudyMind's listening module generates audio at your level and auto-creates questions to test whether you actually understood:</p>
        <p><a class="blog-cta-button" href="/module/listening">🎧 Start listening practice for free →</a></p>

        <blockquote>Listening skill isn't talent — it's 15 minutes a day, compounded.</blockquote>
      `,
    },
  },
};

export default article;
